import { networkClient } from '@network/networkClient'
import { getGlobalParams } from '../globalParams'
import { convertCase } from '@utils/miscUtils'
import {
  INIT_PLAYER,
  PLAYER_SEEDS,
  PLAYER_BETS,
  PLAYER_BET_DETAILS,
  VERIFY_FAIRNESS,
  UPDATE_CLIENT_SEED,
} from '../queries/playerQueries'
import { logger } from '@logger'

const MODULE = 'Player Service'

export const initPlayer = async ({ token, gameCode }) => {
  const response = await networkClient({
    request: {
      query: INIT_PLAYER,
      variables: {
        token,
        gameCode: convertCase({ value: gameCode, mode: 'UPPER' }),
      },
    },
  })

  logger.info(MODULE, 'PlayerService Response', response)

  if (response.init.success) {
    return response.init.data
  } else {
    throw new Error(response.init.message || 'Failed to initialize player session')
  }
}

// Current provably-fair seeds (auth required).
export const fetchPlayerSeeds = async () => {
  const response = await networkClient({
    request: { query: PLAYER_SEEDS, isAuthRequired: true },
  })

  logger.info(MODULE, 'fetchPlayerSeeds Response', response)

  if (response.players.seeds.success) {
    return response.players.seeds.data
  } else {
    throw new Error(response.players.seeds.message || 'Failed to fetch player seeds')
  }
}

// Paginated bet history. Globals come from the session; the component passes only
// its filters + paging. Array filter axes accept ["ALL"], [] or omit to skip.
// Page info is echoed back so the mapper can derive pageCount without the component.
export const fetchPlayerBets = async ({
  type,
  subType,
  source,
  status,
  page = 1,
  pageSize = 10,
} = {}) => {
  const { operatorId, brandId, currency, playerId, gameCode } = getGlobalParams()

  const response = await networkClient({
    request: {
      query: PLAYER_BETS,
      variables: {
        operatorId,
        brandId,
        currency,
        playerId,
        gameCode,
        transactionType: type,
        transactionSubType: subType,
        transactionSourceType: source,
        status,
        page,
        limit: pageSize,
      },
    },
  })

  logger.info(MODULE, 'fetchPlayerBets Response', response)

  if (response.players.bets.success) {
    return { ...response.players.bets.data, page, pageSize }
  } else {
    throw new Error(response.players.bets.message || 'Failed to fetch bet history')
  }
}

// Single bet detail with fairness data (hash/seed revealed after settlement).
export const fetchBetDetails = async ({ transactionId } = {}) => {
  const response = await networkClient({
    request: { query: PLAYER_BET_DETAILS, variables: { transactionId } },
  })

  logger.info(MODULE, 'fetchBetDetails Response', response)

  if (response.players.betDetails.success) {
    return response.players.betDetails.data
  } else {
    throw new Error(response.players.betDetails.message || 'Failed to fetch bet details')
  }
}

// Recompute a crash multiplier from a revealed hash + seed. gameCode/gameMode
// come from the session; the component passes only { hash, seed }.
export const verifyFairness = async ({ hash, seed } = {}) => {
  const { gameCode, gameMode } = getGlobalParams()

  const response = await networkClient({
    request: { query: VERIFY_FAIRNESS, variables: { hash, seed, gameCode, gameMode } },
  })

  logger.info(MODULE, 'verifyFairness Response', response)

  if (response.players.verifyFairness.success) {
    return response.players.verifyFairness.data
  } else {
    throw new Error(response.players.verifyFairness.message || 'Failed to verify fairness')
  }
}

// Rotate the client seed (auth required, mutation).
export const updateClientSeed = async ({ clientSeed } = {}) => {
  const response = await networkClient({
    request: { query: UPDATE_CLIENT_SEED, variables: { clientSeed }, isAuthRequired: true },
  })

  logger.info(MODULE, 'updateClientSeed Response', response)

  if (response.players.updateClientSeed.success) {
    return response.players.updateClientSeed.data
  } else {
    throw new Error(response.players.updateClientSeed.message || 'Failed to update client seed')
  }
}
